/**
 * Adresses qui portent un secret (jeton de réinitialisation, d'invitation, de lien de
 * paiement, de confirmation de rendez-vous). Ces valeurs ne doivent jamais quitter
 * l'application vers un tiers : Sentry (événements, fils d'Ariane, transactions) les
 * masque avec `scrubSecretUrl`, et le chargeur GTM (public/gtm-loader.js) ne se charge
 * pas sur ces pages.
 *
 * ⚠️ `SECRET_PATH_PREFIXES` est recopiée dans public/gtm-loader.js (fichier servi tel
 * quel, hors bundle, qui ne peut rien importer). Le test
 * src/__tests__/gtmLoaderSecretRoutes.test.ts rougit si les deux listes divergent.
 */

/** Préfixes de chemin dont le segment suivant est un secret. La barre finale est requise. */
export const SECRET_PATH_PREFIXES = ['/invite/', '/pay/', '/confirmer-rdv/', '/en/confirmer-rdv/'] as const;

/** Paramètres de requête ou de fragment dont la valeur est un secret. */
export const SECRET_QUERY_PARAMS = ['token', 'session_id', 'code', 'access_token', 'refresh_token'] as const;

export const REDACTED = '[redacted]';

const isSecretParamName = (rawName: string): boolean => {
  let name = rawName;
  try {
    name = decodeURIComponent(rawName);
  } catch {
    // nom malformé : on le compare tel quel
  }
  // Insensible à la casse : une requête `?Token=` porte le même secret.
  return (SECRET_QUERY_PARAMS as readonly string[]).includes(name.toLowerCase());
};

/** Préfixe comparé sans tenir compte de la casse (React Router fait pareil sur les chemins). */
const startsWithPrefix = (path: string, prefix: string): boolean =>
  path.slice(0, prefix.length).toLowerCase() === prefix;

/** Masque la valeur des paramètres secrets d'une chaîne `a=b&c=d`. */
const scrubParams = (params: string): string =>
  params
    .split('&')
    .map((part) => {
      const eq = part.indexOf('=');
      if (eq <= 0 || eq === part.length - 1) return part;
      return isSecretParamName(part.slice(0, eq)) ? `${part.slice(0, eq)}=${REDACTED}` : part;
    })
    .join('&');

/** Masque le segment qui suit un préfixe secret ; le reste du chemin est gardé. */
const scrubPath = (path: string): string => {
  for (const prefix of SECRET_PATH_PREFIXES) {
    if (!startsWithPrefix(path, prefix)) continue;
    const rest = path.slice(prefix.length);
    const slash = rest.indexOf('/');
    const segment = slash === -1 ? rest : rest.slice(0, slash);
    if (!segment) return path;
    return path.slice(0, prefix.length) + REDACTED + (slash === -1 ? '' : rest.slice(slash));
  }
  return path;
};

/**
 * Rend l'adresse sans ses secrets. Accepte une adresse absolue ou relative. Une adresse
 * sans secret ressort STRICTEMENT identique (même chaîne). Une entrée inanalysable ou non
 * textuelle est rendue telle quelle, sans lever. Idempotente. Le préfixe est comparé sur
 * le chemin seulement, jamais sur l'hôte.
 */
export function scrubSecretUrl(url: string): string {
  try {
    if (typeof url !== 'string' || url === '') return url;

    const hashAt = url.indexOf('#');
    const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
    const hash = hashAt === -1 ? null : url.slice(hashAt + 1);

    const queryAt = beforeHash.indexOf('?');
    const beforeQuery = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
    const query = queryAt === -1 ? null : beforeHash.slice(queryAt + 1);

    const origin = /^[a-z][a-z0-9+.-]*:\/\/[^/]*/i.exec(beforeQuery)?.[0] ?? '';
    const path = beforeQuery.slice(origin.length);

    let out = origin + scrubPath(path);
    if (query !== null) out += `?${scrubParams(query)}`;
    if (hash !== null) out += `#${hash.includes('=') ? scrubParams(hash) : hash}`;
    return out;
  } catch {
    return url;
  }
}

/**
 * Vrai quand la page d'arrivée porte un secret : chemin à préfixe secret, ou paramètre
 * `token`. Même règle que public/gtm-loader.js (qui n'y met pas `session_id`).
 */
export function urlCarriesSecret(pathname: string, search: string): boolean {
  if (SECRET_PATH_PREFIXES.some((prefix) => startsWithPrefix(pathname, prefix))) return true;
  const query = search.startsWith('?') ? search.slice(1) : search;
  return query.split('&').some((part) => part.split('=')[0].toLowerCase() === 'token');
}

/**
 * Filtre `beforeSend` de `<Analytics />` (@vercel/analytics) : l'adresse de chaque page vue
 * part chez Vercel, jeton compris. Rend l'événement avec son `url` masquée, tous les autres
 * champs intacts.
 */
export function scrubVercelAnalyticsEvent<T extends { url: string }>(event: T): T {
  return { ...event, url: scrubSecretUrl(event.url) };
}
